import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ReportsService } from '../ReportsService';
import { NavLink, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row, Tooltip } from 'react-bootstrap';
import { IReportItem } from '../../../model/report/Report-item';
import { FaArrowAltCircleLeft, FaArrowAltCircleRight } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { ImpersonateEye } from '../../../shared/components/ImpersonateEye';
import { formatDate, getCorrectEnums, getPlatformValue } from '../../../utils/Functions';
import { CustomImageHandler } from '../../../shared/components/img/CustomImageHandler';
import { UPDATED_REPORT_ITEM } from '../../../enum/Messages';
import CustomOverlayTrigger from '../../../shared/components/CustomOverlayTrigger';
import ModalReportReject from './ModalReportReject';
import ModalReportConfirm from './ModalReportConfirm';
import CustomFancybox from '../../../shared/components/Fancybox';
import { ErrorToast } from '../../../utils/toasters';

export const initialReport: IReportItem = {
    id: 0,
    additionalPost: false,
    isVerified: false,
    isRejected: false,
    createdAt: '',
    updatedAt: '',
    reach: 0,
    likes: 0,
    dislikes: 0,
    views: 0,
    watchTime: 0,
    shares: 0,
    saves: 0,
    impressions: 0,
    swipeUpLinks: 0,
    stickerEngagements: 0,
    reportImages: [],
    event: {},
    offer: {},
    socialPlatform: {},
    nextItem: 0,
    previousItem: 0,
};

export const reportingFields = [
    'views',
    'reach',
    'impressions',
    'likes',
    'comments',
    'clicks',
    'stickerEngagements',
    'swipeUpLinks',
    'shares',
    'saves',
    'watchTime',
    'dislikes',
    'nbPublishedPosts',
    'completedViews',
    'videoLength',
    'reposts',
];

export function ReportsForm() {
    const [report, setReport] = useState<IReportItem>(initialReport);
    const { reportId } = useParams<{ reportId: string }>();
    const [modalData, setModalData] = useState({
        showModal: false,
    });
    const [confirmModalData, setConfirmModalData] = useState({
        showModal: false,
    });

    useEffect(() => {
        ReportsService.getReportItem(+reportId!)
            .then(data => {
                setReport(data);
            })
            .catch(err => ErrorToast(err));
    }, [reportId]);

    const updateReport = () => {
        ReportsService.updateReport(+reportId!, report)
            .then(data => {
                setReport(data);
                toast(UPDATED_REPORT_ITEM);
                setConfirmModalData((prev: any) => ({ ...prev, showModal: false }));
            })
            .catch(err => ErrorToast(err));
    };

    const rejectReport = (updatedReport: IReportItem) => {
        ReportsService.rejectReport(+reportId!, updatedReport)
            .then(data => {
                setReport(data);
                toast(UPDATED_REPORT_ITEM);
                setModalData((prev: any) => ({ ...prev, showModal: false }));
            })
            .catch(err => ErrorToast(err));
    };

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const rawValue = event.target.value;

        const isValidNumberInput = /^-?\d*\.?\d*$/.test(rawValue);

        if (!isValidNumberInput) {
            return; 
        }

        setReport({
            ...report,
            [event.target.name]: rawValue === '' ? '' : Number(rawValue),
        });
    }
    return (
        <div>
            <ModalReportReject {...modalData} setModalData={setModalData} rejectReport={rejectReport} report={report} />
            <ModalReportConfirm {...confirmModalData} setModalData={setConfirmModalData} updateReport={updateReport} />
            <div className="d-flex justify-content-between mb-4 align-items-center">
                {report?.previousItem ? (
                    <NavLink
                        className="col-2 font-size-h4"
                        to={{
                            pathname: '/reporting/edit/' + report.previousItem,
                        }}
                    >
                        <FaArrowAltCircleLeft />
                        <span className="align-middle pl-2">Previous {report?.previousItem}</span>
                    </NavLink>
                ) : (
                    <span className="col-2" />
                )}
                <h3 className="col-8 text-center mb-0">
                    {report?.socialPlatform ? (
                        <>
                            <img
                                alt=""
                                src={report?.socialPlatform?.path}
                                style={{ width: 40, height: 40 }}
                                className="rounded"
                            />
                            <span className="ml-1">
                                {getPlatformValue(report?.socialPlatform?.name)}{' '}
                                {!report?.additionalPost
                                    ? `${report?.event?.platformPosition ?? '-'}/${report?.event?.offerItem?.nbPosts}`
                                    : 'Additional'}
                            </span>
                        </>
                    ) : (
                        <img
                            alt=""
                            src="/images/placeholder.png"
                            style={{ width: 40, height: 40 }}
                            className="rounded"
                        />
                    )}{' '}
                    | Campaign: {report?.offer?.campaign?.id}
                    <CustomOverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="campaign-tooltip">{report?.offer?.campaign?.title}</Tooltip>}
                    >
                        <span>
                            <FiInfo size={14} className="cursor-pointer" />
                        </span>
                    </CustomOverlayTrigger>{' '}
                    | Offer: {report?.offer?.id} | User: {report?.offer?.user?.id}
                    <CustomOverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="user-tooltip">{report?.offer?.user?.displayName}</Tooltip>}
                    >
                        <span>
                            <FiInfo size={14} className="cursor-pointer" />
                        </span>
                    </CustomOverlayTrigger>
                    <ImpersonateEye id={report?.offer?.user?.id} />
                </h3>
                {report?.nextItem ? (
                    <NavLink
                        className="text-right col-2 font-size-h4"
                        to={{
                            pathname: '/reporting/edit/' + report.nextItem,
                        }}
                    >
                        <span className="align-middle pr-2">Next {report?.nextItem}</span>
                        <FaArrowAltCircleRight />
                    </NavLink>
                ) : (
                    <span className="col-2" />
                )}
            </div>

            <Card className="mb-5">
                <Form as={Card.Body}>
                    {report?.offer?.user?.locale && (
                        <h4 className="mb-4">
                            Influencer language: {report?.offer?.user?.locale === 'de' ? 'German' : 'English'}
                        </h4>
                    )}
                    <Row className="d-flex justify-content-between">
                        <Col md={4}>
                            <Row>
                                {reportingFields
                                    ?.filter(field => report?.socialPlatform?.[field as keyof IReportItem])
                                    ?.map(field => (
                                        <Col md={6} sm={8} xs={12} key={field}>
                                            <Form.Group>
                                                <Form.Label>{getCorrectEnums(field)}</Form.Label>
                                                <Form.Control
                                                    name={field}
                                                    onChange={handleChange}
                                                    value={report?.[field as keyof IReportItem] ?? ''}
                                                    type="text"
                                                    inputMode="numeric"
                                                />
                                            </Form.Group>
                                        </Col>
                                    ))}
                                {/*<Button onClick={updateReport} className="col-md-12 col-sm-12 mt-3" size='lg'>*/}
                                {/*    APPROVE & UPDATE*/}
                                {/*</Button>*/}
                                <div>
                                    <Button
                                        onClick={() =>
                                            setConfirmModalData((prev: any) => ({ ...prev, showModal: true }))
                                        }
                                        className="col-md-12 col-sm-12 mt-3"
                                        size="lg"
                                    >
                                        APPROVE & UPDATE
                                    </Button>
                                    <Button
                                        onClick={() => setModalData((prev: any) => ({ ...prev, showModal: true }))}
                                        className="col-md-12 col-sm-12 mt-3"
                                        size="lg"
                                        variant="outline-primary"
                                    >
                                        REJECT
                                    </Button>
                                </div>
                                {(report?.event?.verifiedBy || report?.approvedBy) && (
                                    <div className="mt-10 d-flex align-items-center col-12" key={report?.id}>
                                        <div className="col">
                                            {report?.event
                                                ? report.event.isVerified
                                                    ? 'Approved by:'
                                                    : 'Rejected by:'
                                                : report.isVerified
                                                ? 'Approved by:'
                                                : 'Rejected by:'}
                                        </div>
                                        <span className="col">
                                            <CustomImageHandler
                                                altTag=""
                                                classes="rounded-circle col-0 img-cover me-2"
                                                thumbnailPath={
                                                    report?.event?.verifiedBy?.profilePhoto?.thumbnail ??
                                                    report?.approvedBy?.profilePhoto?.thumbnail ??
                                                    ''
                                                }
                                                photoPath={
                                                    report?.event?.verifiedBy?.profilePhoto?.path ??
                                                    report?.approvedBy?.profilePhoto?.path ??
                                                    ''
                                                }
                                                style={{ width: 50, height: 50 }}
                                            />
                                            {report?.event?.verifiedBy?.firstName ?? report?.approvedBy?.firstName}
                                        </span>
                                        <span className="col text-end">
                                            {formatDate(report?.event?.verifyDate ?? report?.verifyDate)}
                                        </span>
                                    </div>
                                )}

                                {(report?.event?.isRejected || (report?.isRejected && !report?.event)) &&
                                    report?.rejectReason && (
                                        <div className="mt-10 my-4 d-flex align-items-center col-12 word-break text-danger">
                                            Reject reason: {report.rejectReason}
                                        </div>
                                    )}
                            </Row>
                        </Col>
                        <Col md={8} className="d-flex justify-content-center align-items-center">
                            <CustomFancybox files={report?.reportImages} keyPath="reportPath" />
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
}
